import { $createRangeSelection, $createTextNode, $getRoot, $getSelection, $isLineBreakNode, $isRangeSelection, $setSelection, TextNode } from 'lexical';

import MisspelledNode from './MisspelledNode';

// Lexical convention: $-prefixed functions must run inside editor.update() or editorState.read().
// They access implicit editor state, similar to how React hooks require component context.

export function $createMisspelledNode(text) {
  return new MisspelledNode(text);
}

export function $isMisspelledNode(node) {
  return node instanceof MisspelledNode;
}

export function getAllDescendants(node, callback) {
  if (!node.getChildren) return;

  const children = node.getChildren();
  for (const child of children) {
    callback(child);
    getAllDescendants(child, callback);
  }
}

export function transformApiMatches(matches, text) {
  if (!matches) return [];

  return matches.map((match) => ({
    position: match.offset,
    length: match.length,
    word: text.substring(match.offset, match.offset + match.length),
    suggestions: match.replacements ? match.replacements.map((r) => r.value) : [],
    reason: match.shortMessage || match.message || 'Spelling mistake',
  }));
}

export function replaceMisspelledWithTextNodes(root) {
  const misspelledNodes = [];

  getAllDescendants(root, (node) => {
    if ($isMisspelledNode(node)) {
      misspelledNodes.push(node);
    }
  });

  misspelledNodes.forEach((node) => {
    const textNode = $createTextNode(node.getTextContent());
    node.replace(textNode);
  });
}

/**
 * Finds the TextNode that contains text at the given absolute position.
 * Paragraph-aware: accounts for \n between paragraphs.
 */
export function findNodeAtPosition(root, position) {
  const paragraphs = root.getChildren();
  let currentOffset = 0;

  for (let i = 0; i < paragraphs.length; i++) {
    if (i > 0) currentOffset += 1; // \n between paragraphs

    const paragraph = paragraphs[i];
    const children = paragraph.getChildren ? paragraph.getChildren() : [];

    for (const child of children) {
      if ($isLineBreakNode(child)) {
        currentOffset += 1;
        continue;
      }
      // Skip MisspelledNodes -- we only split plain TextNodes; misspelled spans get cleared first
      if (!(child instanceof TextNode) || $isMisspelledNode(child)) continue;

      const text = child.getTextContent();
      if (position >= currentOffset && position < currentOffset + text.length) {
        return { node: child, offset: position - currentOffset };
      }
      currentOffset += text.length;
    }
  }

  return null;
}

// Converts a Lexical PointType (node-relative) to an absolute character offset across the document
function $nodePointToAbsoluteOffset(point) {
  const root = $getRoot();
  const paragraphs = root.getChildren();
  let offset = 0;

  for (let i = 0; i < paragraphs.length; i++) {
    if (i > 0) offset += 1;

    const paragraph = paragraphs[i];
    const children = paragraph.getChildren ? paragraph.getChildren() : [];

    for (const child of children) {
      if (child.getKey() === point.key) {
        return offset + point.offset;
      }
      if ($isLineBreakNode(child)) {
        offset += 1;
      } else {
        offset += child.getTextContent().length;
      }
    }
  }

  return offset;
}

// Converts an absolute character offset back to a Lexical-compatible { key, offset, type } point
function $absoluteOffsetToNodePoint(root, targetOffset) {
  const paragraphs = root.getChildren();
  let currentOffset = 0;

  for (let i = 0; i < paragraphs.length; i++) {
    if (i > 0) {
      if (targetOffset === currentOffset) {
        const children = paragraphs[i].getChildren ? paragraphs[i].getChildren() : [];
        for (const child of children) {
          if ($isLineBreakNode(child)) continue;
          if (child instanceof TextNode) {
            return { key: child.getKey(), offset: 0, type: 'text' };
          }
        }
      }
      currentOffset += 1;
    }

    const paragraph = paragraphs[i];
    const children = paragraph.getChildren ? paragraph.getChildren() : [];

    if (children.length === 0) {
      continue;
    }

    for (const child of children) {
      if ($isLineBreakNode(child)) {
        if (targetOffset === currentOffset) {
          return { key: paragraph.getKey(), offset: currentOffset, type: 'element' };
        }
        currentOffset += 1;
        continue;
      }

      if (!(child instanceof TextNode)) continue;

      const len = child.getTextContent().length;
      if (targetOffset >= currentOffset && targetOffset <= currentOffset + len) {
        return { key: child.getKey(), offset: targetOffset - currentOffset, type: 'text' };
      }
      currentOffset += len;
    }
  }

  // Clamp to end of last text node
  for (let i = paragraphs.length - 1; i >= 0; i--) {
    const children = paragraphs[i].getChildren ? paragraphs[i].getChildren() : [];
    for (let j = children.length - 1; j >= 0; j--) {
      const child = children[j];
      if ($isLineBreakNode(child)) continue;
      if (child instanceof TextNode) {
        return { key: child.getKey(), offset: child.getTextContent().length, type: 'text' };
      }
    }
  }

  return null;
}

/**
 * Saves current Lexical selection as absolute character offsets.
 * Returns { anchor, focus } or null if no selection.
 */
export function $saveSelectionAsOffset() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;

  return {
    anchor: $nodePointToAbsoluteOffset(selection.anchor),
    focus: $nodePointToAbsoluteOffset(selection.focus),
  };
}

/**
 * Restores a selection from saved absolute character offsets.
 */
export function $restoreSelectionFromOffset(saved) {
  if (!saved) return;

  const root = $getRoot();
  const anchorPoint = $absoluteOffsetToNodePoint(root, saved.anchor);
  const focusPoint = $absoluteOffsetToNodePoint(root, saved.focus);
  if (!anchorPoint || !focusPoint) return;

  let selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    selection = $createRangeSelection();
  }

  selection.anchor.set(anchorPoint.key, anchorPoint.offset, anchorPoint.type);
  selection.focus.set(focusPoint.key, focusPoint.offset, focusPoint.type);
  $setSelection(selection);
}
